import taskManagerApi from "@/features/shared/services/taskManagerApi";
import { transformTaskToRequest } from "@/features/shared/utils/helpers";
/**
 * Fetch tasks without any filters or pagination.
 * @returns Promise resolving to TaskData.
 */
export const getTasks = async () => {
    try {
        const response = await taskManagerApi.get("/task", {
            params: { include_custom_fields: true },
        });
        // Expect response.data.data to have both tasks and meta.
        return response.data?.data;
    }
    catch (error) {
        throw new Error("Impossible de récupérer les tâches : " + error);
    }
};
/**
 * Fetch all tasks (without pagination).
 * @returns Promise resolving to TaskData.
 */
export const getAllTasks = async (filters) => {
    try {
        const params = {
            ...(filters || {}),
            ...(filters.parent ? { parent: filters.parent } : {}),
            all: true,
            include_custom_fields: true,
        };
        const response = await taskManagerApi.get("/task", { params });
        return response.data?.data;
    }
    catch (error) {
        throw new Error("Impossible de récupérer toutes les tâches.");
    }
};
/**
 * Fetch tasks with filters and pagination.
 * @param page - Page number.
 * @param filters - Filtering criteria.
 * @returns Promise resolving to TaskData.
 */
export const getTasksWithFilters = async (page, filters, limit) => {
    try {
        const params = {
            ...(filters || {}),
            ...(filters.parent ? { parent: filters.parent } : {}),
            page,
            ...(limit ? { limit } : {}),
            include_custom_fields: true,
        };
        const response = await taskManagerApi.get("/task", { params });
        return response.data?.data;
    }
    catch (error) {
        throw new Error("Impossible de récupérer les tâches : " + error);
    }
};
/**
 * Advance the state of a task.
 * @param taskId - ID of the task.
 */
export const advanceTask = async (taskId) => {
    try {
        await taskManagerApi.put(`/task/${taskId}/advance`);
    }
    catch (error) {
        throw new Error("Impossible d'avancer la tâche.");
    }
};
/**
 * Change the state of a task manually.
 * @param taskId - ID of the task.
 * @param stateCode - The new state code.
 */
export const changeTaskState = async (taskId, stateCode) => {
    try {
        await taskManagerApi.put(`/task/${taskId}/status`, { status: stateCode });
    }
    catch (error) {
        throw new Error("Impossible de modifier l'état de la tâche.");
    }
};
/**
 * Change the state of a task to to_do and reopen existing assignments
 * @param taskId - ID of the task.
 */
export const reOpenTask = async (taskId) => {
    try {
        await taskManagerApi.put(`/task/${taskId}/reopen`);
    }
    catch (error) {
        throw new Error("Impossible de réouvrir la tâche.");
    }
};
/**
 * Update an existing task.
 * @param taskId - ID of the task.
 * @param task - The task object to update.
 * @returns Updated task (if returned by API) or undefined.
 */
export const updateTask = async (taskId, task) => {
    try {
        const response = await taskManagerApi.put(`/task/${taskId}`, transformTaskToRequest(task));
        return response.data?.task;
    }
    catch (error) {
        throw new Error("Impossible de mettre à jour la tâche : " + error);
    }
};
/**
 * Add a new task.
 * @param task - The task object to create.
 * @returns The new task's ID.
 */
export const addTask = async (task) => {
    try {
        const response = await taskManagerApi.post("/task", transformTaskToRequest(task));
        return response.data?.data?.task?.id;
    }
    catch (error) {
        throw new Error("Impossible de créer la tâche." + error);
    }
};
/**
 * Delete a task.
 * @param taskId - ID of the task to delete.
 */
export const deleteTask = async (taskId) => {
    try {
        await taskManagerApi.delete(`/task/${taskId}`);
    }
    catch (error) {
        throw new Error("Impossible de supprimer la tâche.");
    }
};
/**
 * Notify backend that a subtask has been completed and
 * trigger parent status update when all subtasks are done.
 * @param parentId - ID of the parent task.
 * @param subtaskId - ID of the completed subtask.
 */
export const syncParentOnSubtaskDone = async (parentId, subtaskId) => {
    try {
        await taskManagerApi.put(`/task/${parentId}/subtasks/${subtaskId}/done`);
    }
    catch (error) {
        throw new Error("Impossible de mettre à jour la tâche parente.");
    }
};
/**
 * Notify backend that a subtask has been reopened and
 * trigger parent status update when necessary.
 * @param parentId - ID of the parent task.
 * @param subtaskId - ID of the reopened subtask.
 */
export const syncParentOnSubtaskReopen = async (parentId, subtaskId) => {
    try {
        await taskManagerApi.put(`/task/${parentId}/subtasks/${subtaskId}/reopen`);
    }
    catch (error) {
        throw new Error("Impossible de mettre à jour la tâche parente.");
    }
};
export const sendTaskReminder = async (taskId, userId) => {
    try {
        await taskManagerApi.post(`/task/${taskId}/remind`, { user_id: userId });
    }
    catch (error) {
        const message = error?.response?.data?.message ?? "Impossible d'envoyer le rappel.";
        throw new Error(message);
    }
};
